import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { AppHttpHeadersService } from '@service/rest/headers/app-http-headers.service';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class SettingsInfoService {
  constructor(
    private http: HttpClient,
    private appHttpHeadersService: AppHttpHeadersService
  ) {}

  checkPassword(formData): Observable<any> {
    return this.http.post(environment.gateway_api + '/accounts/check-password', formData, { headers: this.appHttpHeadersService.getHeaders() });
  }

  getProfileImage(email): Observable<any> {
    return this.http.get(environment.gateway_api + '/emails/settings/photo', { headers: this.appHttpHeadersService.getHeaders() });
  }

  deleteProfileImage(): Observable<any> {
    return this.http.delete(environment.gateway_api + '/emails/settings/photo', { headers: this.appHttpHeadersService.getHeaders() });
  }

  saveSignature(payload: FormData): Observable<{ content: string }> {
    return this.http.post<{ content: string }>(environment.gateway_api + '/accounts/signature  ', payload, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  getSettings(): Observable<any> {
    return this.http.get(environment.gateway_api + '/emails/settings', { headers: this.appHttpHeadersService.getV2AuthHeaders() });
  }

  updateSettings(formData): Observable<any> {
    return this.http.put(environment.gateway_api + '/emails/settings', formData, { headers: this.appHttpHeadersService.getHeaders() });
  }

  getEmailInformation(): Observable<any> {
    return this.http.get(environment.gateway_api + '/emails/info', { headers: this.appHttpHeadersService.getHeaders() });
  }

  accountUpdate(formData): Observable<any> {
    return this.http.put(environment.gateway_api + '/accounts/update', formData, { headers: this.appHttpHeadersService.getHeaders() });
  }

  saveMsisdn(password: string, sub: string, msisdn: string, session_id: string): Observable<any> {
    return this.http.put(
      environment.gateway_api + '/accounts/update',
      { password: password, sub: sub, msisdn: msisdn },
      { headers: this.appHttpHeadersService.getMsisdnSessionHeaders(session_id) }
    );
  }

  getDevices(): Observable<any> {
    return this.http.get(environment.gateway_api + '/tokens/list', { headers: this.appHttpHeadersService.getHeaders() });
  }

  removeDevice(token: string): Observable<any> {
    const url = environment.gateway_api + '/tokens/' + token;
    return this.http.delete<any>(url, { headers: this.appHttpHeadersService.getHeaders() });
  }

  removeDevices(tokens: string[]): Observable<any> {
    const options = {
      headers: this.appHttpHeadersService.getHeaders(),
      body: {
        tokens: tokens,
      },
    };
    return this.http.delete(environment.gateway_api + '/tokens', options);
  }

  getCountry(ip: string): Observable<any> {
    const url = environment.gateway_api + `/services/ip/${ip}`;
    return this.http.get(url, { headers: this.appHttpHeadersService.getHeaders() });
  }

  saveProfileImage(formData): Observable<any> {
    return this.http.post(environment.gateway_api + '/emails/settings/photo', formData, { headers: this.appHttpHeadersService.getHeaders() });
  }

  sendVerificationEmail(): Observable<any> {
    return this.http.post(environment.gateway_api + '/emails/validation/other-email', {}, { headers: this.appHttpHeadersService.getHeaders() });
  }

  /**
   * Gets blocked senders
   */
  getBlockedSenders(): Observable<any> {
    return this.http.get(environment.gateway_api + '/emails/blocked-sender', { headers: this.appHttpHeadersService.getHeaders() });
  }

  /**
   * Gets safe senders
   */
  getSafeSenders(): Observable<any> {
    return this.http.get(environment.gateway_api + '/emails/safe-sender', { headers: this.appHttpHeadersService.getHeaders() });
  }

  /**
   * Blocks a sender
   * @param data
   */
  blockSender(emails: string[]): Observable<any> {
    return this.http.put(
      environment.gateway_api + '/emails/blocked-sender',
      { emails: emails },
      { headers: this.appHttpHeadersService.getHeaders() }
    );
  }

  /**
   * Safe a sender
   * @param data
   */
  safeSender(emails: string[]): Observable<any> {
    return this.http.put(environment.gateway_api + '/emails/safe-sender', { emails: emails }, { headers: this.appHttpHeadersService.getHeaders() });
  }

  /**
   * Deletes blocked sender
   * @param data
   */
  deleteBlockedSender(emails: string[]): Observable<any> {
    const options = {
      headers: this.appHttpHeadersService.getHeaders(),
      body: {
        emails: emails,
      },
    };
    return this.http.delete(environment.gateway_api + '/emails/blocked-sender', options);
  }

  deleteSafeSender(emails: string[]): Observable<any> {
    const options = {
      headers: this.appHttpHeadersService.getHeaders(),
      body: {
        emails: emails,
      },
    };
    return this.http.delete(environment.gateway_api + '/emails/safe-sender', options);
  }

  changePassword(data: any): Observable<any> {
    return this.http.put(environment.gateway_api + '/emails/settings/password', data, { headers: this.appHttpHeadersService.getHeaders() });
  }

  deleteAccount(formData): Observable<any> {
    return this.http.post(environment.gateway_api + '/accounts/close', formData, { headers: this.appHttpHeadersService.getHeaders() });
  }

  getEmailsForwarding(): Observable<any> {
    return this.http.get(environment.gateway_api + '/emails/forwarding', { headers: this.appHttpHeadersService.getHeaders() });
  }

  updateEmailsForwarding(formData): Observable<any> {
    return this.http.put(environment.gateway_api + '/emails/forwarding', formData, { headers: this.appHttpHeadersService.getHeaders() });
  }

  checkEmailsForwarding(formData): Observable<any> {
    return this.http.post(environment.gateway_api + '/emails/forwarding/check', formData);
  }

  revalidateForwarding(): Observable<any> {
    return this.http.post(environment.gateway_api + '/emails/forwarding/re-validation', {}, { headers: this.appHttpHeadersService.getHeaders() });
  }

  getNotificationPreference(): Observable<any> {
    return this.http.get(environment.gateway_api + '/accounts/notification-preference', { headers: this.appHttpHeadersService.getHeaders() });
  }

  setNotificationPreference(data): Observable<any> {
    return this.http.put(environment.gateway_api + '/accounts/notification-preference', data, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  getTrustedSenders(queryParams?: string): Observable<any> {
    const queryParamsVal = queryParams || '';
    return this.http.get(environment.gateway_api + '/accounts/trusted-senders' + queryParamsVal, {
      headers: this.appHttpHeadersService.getHeaders(),
    });
  }

  removeTrustedSender(email: string): Observable<any> {
    return this.http.delete(environment.gateway_api + '/accounts/trusted-senders/' + email, { headers: this.appHttpHeadersService.getHeaders() });
  }

  createTrustedSender(formData): Observable<any> {
    return this.http.post(environment.gateway_api + '/accounts/trusted-senders', formData, { headers: this.appHttpHeadersService.getHeaders() });
  }
}
